import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PaEmptyState, PaSkeleton } from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import { fetchPlatformAiUsageTenant } from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_AI_USAGE_TENANTS } from "store/services/URL";
import AiUsageFilterBar from "./AiUsageFilterBar";
import AiUsageKpiStrip from "./AiUsageKpiStrip";
import AiUsageInsightsStrip from "./AiUsageInsightsStrip";
import PlatformAiCostOverTimeChart from "./PlatformAiCostOverTimeChart";
import PlatformAiUsageByFeatureChart from "./PlatformAiUsageByFeatureChart";
import PlatformAiUsageByModelChart from "./PlatformAiUsageByModelChart";
import PlatformAiUsageByStatusChart from "./PlatformAiUsageByStatusChart";
import {
  availableAiUsageGranularities,
  buildAiUsageTenantDetailQuery,
  createAiUsageFiltersForPreset,
  createInitialAiUsageFilters,
  formatAiUsageAud,
  formatAiUsageCount,
  formatAiUsageMeta,
  formatAiUsageTimestamp,
  normalizeByFeatureItems,
  normalizeByUserRows,
  normalizeCostSeries,
  resolveAiUsageDateRange,
  resolveAiUsageGranularity,
} from "./aiUsageOverview.utils";
import { formatFeatureLabel } from "pages/ServiceProvider/SpSetting/AiCreditsUsage/aiUsageLabels";

/** One row per Log ID — bulk timesheet can return the same usage_log_id many times. */
export const uniqueExchangeLogs = (entries = []) => {
  const seen = new Set();
  const unique = [];
  for (const entry of entries || []) {
    const id = entry?.usage_log_id;
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    unique.push(entry);
  }
  return unique;
};

const TenantAiUsageTab = ({ tenantId }) => {
  const canViewAiUsage = platformRoleAccess("platform.ai_usage.view");
  const [filters, setFilters] = useState(() => createInitialAiUsageFilters({ preset: "last30" }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const dateRange = useMemo(() => resolveAiUsageDateRange(filters), [filters]);

  const granularity = useMemo(
    () =>
      detail?.cost_series?.granularity ||
      resolveAiUsageGranularity(filters, dateRange.from, dateRange.to),
    [detail?.cost_series?.granularity, filters, dateRange.from, dateRange.to]
  );

  const granularityOptions = useMemo(
    () => availableAiUsageGranularities(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  );

  const periodLabel = useMemo(
    () => formatAiUsageMeta({ from: dateRange.from, to: dateRange.to }),
    [dateRange.from, dateRange.to]
  );

  const loadDetail = useCallback(async () => {
    if (!canViewAiUsage || !tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildAiUsageTenantDetailQuery(
        filters,
        tenantId,
        URL_PLATFORM_ADMIN_AI_USAGE_TENANTS
      );
      const data = await fetchPlatformAiUsageTenant(endpoint);
      setDetail(data);
    } catch (err) {
      setDetail(null);
      setError(err?.message || "Failed to load tenant AI usage");
    } finally {
      setLoading(false);
    }
  }, [canViewAiUsage, tenantId, filters]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handlePresetChange = (presetId) => {
    setFilters((prev) => createAiUsageFiltersForPreset(presetId, prev));
  };

  const handleGranularityChange = (nextGranularity) => {
    setFilters((prev) => ({ ...prev, granularity: nextGranularity }));
  };

  const initialLoading = loading && !detail;

  if (!canViewAiUsage) {
    return (
      <PaEmptyState
        title="AI usage unavailable"
        message="You need platform.ai_usage.view to see tenant AI spend."
      />
    );
  }

  const costSeries = normalizeCostSeries(detail?.cost_series);
  const byFeatureItems = normalizeByFeatureItems(detail?.by_feature);
  const byUserRows = normalizeByUserRows(detail?.by_user ?? []);

  return (
    <div className="space-y-6">
      <AiUsageFilterBar
        filters={filters}
        granularity={granularity}
        granularityOptions={granularityOptions}
        onPresetChange={handlePresetChange}
        onGranularityChange={handleGranularityChange}
        onCareSectorChange={() => {}}
        showSector={false}
        disabled={initialLoading}
        idPrefix="tenant-ai-usage"
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {initialLoading ? (
        <div className="space-y-4">
          <PaSkeleton variant="card" lines={3} />
          <PaSkeleton variant="card" lines={4} />
        </div>
      ) : !detail ? (
        <PaEmptyState
          title="No AI usage data"
          message="This tenant has no AI activity for the selected period."
        />
      ) : (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Wallet</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatAiUsageAud(detail.wallet?.balance_aud)}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  AI {detail.wallet?.ai_enabled ? "enabled" : "disabled"} · {periodLabel}
                </p>
              </div>
              <Link
                to="/ai-usage"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
              >
                Platform AI overview →
              </Link>
            </div>

            {detail.recent_grants?.length ? (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Recent grants
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-700">
                  {detail.recent_grants.map((grant, index) => (
                    <li key={`${grant.created_at}-${index}`}>
                      {formatAiUsageAud(grant.amount_aud)} · {grant.source || grant.type || "grant"} ·{" "}
                      {formatAiUsageTimestamp(grant.created_at) || "—"}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <AiUsageKpiStrip
            totals={{
              spent_aud: detail.summary?.spent_aud,
              call_count: detail.summary?.call_count,
              input_tokens: detail.summary?.input_tokens,
              output_tokens: detail.summary?.output_tokens,
              active_tenant_count: 1,
              tenant_count_with_balance: detail.wallet?.balance_aud != null ? 1 : 0,
              spent_usd: detail.summary?.spent_usd,
              failed_count: detail.summary?.failed_count,
              cached_count: detail.summary?.cached_count,
              failed_rate_pct: detail.summary?.failed_rate_pct,
              avg_cost_per_call_aud: detail.summary?.avg_cost_per_call_aud,
              avg_latency_ms: detail.summary?.avg_latency_ms,
              billed_count: detail.summary?.billed_count,
            }}
            comparison={{}}
          />
          <AiUsageInsightsStrip totals={detail.summary} />

          <div className="grid items-start gap-5 xl:grid-cols-2">
            <PlatformAiCostOverTimeChart
              series={costSeries}
              from={dateRange.from}
              to={dateRange.to}
              loading={loading}
              granularity={granularity}
            />
            <PlatformAiUsageByFeatureChart
              items={byFeatureItems}
              trends={detail.feature_trends}
              from={dateRange.from}
              to={dateRange.to}
              loading={loading}
              granularity={granularity}
            />
          </div>

          <div className="grid items-start gap-5 xl:grid-cols-2">
            <PlatformAiUsageByModelChart
              items={detail.by_model}
              trends={detail.model_trends}
              from={dateRange.from}
              to={dateRange.to}
              loading={loading}
              granularity={granularity}
            />
            <PlatformAiUsageByStatusChart
              items={detail.by_status}
              trends={detail.status_trends}
              from={dateRange.from}
              to={dateRange.to}
              loading={loading}
              granularity={granularity}
            />
          </div>

          {byUserRows.length ? (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Top users</h3>
                <p className="text-xs text-gray-500">Highest AI callers in this period</p>
              </div>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">User</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Calls</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Spend (AUD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byUserRows.map((user) => (
                    <tr key={user.user_id ?? user.user_email ?? user.name}>
                      <td className="px-4 py-3 text-gray-900">{user.name || `User #${user.user_id}`}</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {formatAiUsageCount(user.call_count)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {formatAiUsageAud(user.cost_aud)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {detail.exchange_logs?.length ? (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">LLM exchange log</h3>
                <p className="text-xs text-gray-500">
                  Recent AI calls with status and request time (no raw payloads)
                </p>
              </div>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Feature</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Request time</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Log ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {uniqueExchangeLogs(detail.exchange_logs).map((entry) => (
                    <tr key={entry.usage_log_id}>
                      <td className="px-4 py-3 text-gray-900">
                        {formatFeatureLabel({
                          feature_key: entry.feature,
                          feature: entry.feature,
                          prompt_name: entry.prompt_name,
                        })}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{entry.status || "—"}</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {entry.latency_ms != null ? `${entry.latency_ms} ms` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-gray-500">
                        {entry.usage_log_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TenantAiUsageTab;
